import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API, getToken, formatApiErrorDetail, STATUS_LABELS } from "@/lib/api";
import StatusStepper from "@/components/app/StatusStepper";
import { StatusPill, OverdueBadge } from "@/components/app/Badges";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft,
  ImagePlus,
  Loader2,
  Trash2,
  CalendarClock,
  User as UserIcon,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";

export default function ComplaintDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInput = useRef(null);
  const [c, setC] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [zoomed, setZoomed] = useState(null); // photo
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/complaints/${id}`);
      setC(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const onStatusChange = async (s) => {
    if (s === c.status) return;
    try {
      const { data } = await api.patch(`/complaints/${id}/status`, { status: s });
      setC(data);
      toast.success(`Estado: ${STATUS_LABELS[s]}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/complaints/${id}/notes`, { text: noteText });
      setNoteText("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSavingNote(false);
    }
  };

  const onUpload = async (filesList) => {
    const list = Array.from(filesList).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      await api.post(`/complaints/${id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${list.length} foto(s) subida(s)`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const onDeletePhoto = async (photoId) => {
    if (!window.confirm("¿Eliminar esta foto?")) return;
    try {
      await api.delete(`/complaints/${id}/photos/${photoId}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const onDeleteComplaint = async () => {
    if (!window.confirm("¿Eliminar esta reclamación? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/complaints/${id}`);
      toast.success("Reclamación eliminada");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  if (err && !c) {
    return (
      <div className="p-12 text-center text-zinc-600">
        {err} <Link to="/dashboard" className="underline">Volver al panel</Link>
      </div>
    );
  }

  if (!c) {
    return <div className="p-12 text-center text-zinc-500">Cargando…</div>;
  }

  const photoSrc = (photoId) =>
    `${API}/complaints/${id}/photos/${photoId}?auth=${encodeURIComponent(getToken())}`;

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-[1400px] mx-auto" data-testid="complaint-detail-page">
      <button
        onClick={() => navigate("/dashboard")}
        className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black flex items-center gap-2 mb-6"
        data-testid="back-btn"
      >
        <ArrowLeft size={14} /> Volver al panel
      </button>

      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 pb-6 border-b border-black">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            Reclamación
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight font-mono">
            {c.number}
          </h1>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <StatusPill status={c.status} />
            {c.overdue && <OverdueBadge />}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() =>
              window.open(
                `${API}/complaints/${id}/export.pdf?auth=${encodeURIComponent(getToken())}`,
                "_blank"
              )
            }
            variant="outline"
            className="rounded-none border-black h-10 text-xs uppercase tracking-widest"
            data-testid="export-single-pdf-btn"
          >
            <FileDown size={14} className="mr-2" /> Exportar PDF
          </Button>
          {user?.role === "admin" && (
            <Button
              variant="outline"
              onClick={onDeleteComplaint}
              className="rounded-none border-[#E63946] text-[#E63946] hover:bg-[#FFEBEB] h-10 text-xs uppercase tracking-widest"
              data-testid="delete-complaint-btn"
            >
              <Trash2 size={14} className="mr-2" /> Eliminar
            </Button>
          )}
        </div>
      </header>

      {/* Stepper */}
      <section className="border border-black bg-white p-6 mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
          Estado del flujo — haz clic para avanzar
        </div>
        <StatusStepper current={c.status} onChange={onStatusChange} />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: metadata + timeline */}
        <div className="lg:col-span-3 space-y-8">
          <section className="border border-black bg-white">
            <div className="border-b border-black p-4 font-display font-semibold text-sm uppercase tracking-widest">
              Información
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-zinc-200">
              <div className="p-5">
                <dt className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Conductor reclamado
                </dt>
                <dd className="font-display text-xl font-semibold mt-1 flex items-center gap-2">
                  <UserIcon size={16} /> {c.driver_name}
                </dd>
              </div>
              <div className="p-5">
                <dt className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Creada por
                </dt>
                <dd className="font-display text-xl font-semibold mt-1">
                  {c.created_by_name}
                </dd>
              </div>
              <div className="p-5 border-t border-zinc-200">
                <dt className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Fecha de la reclamación
                </dt>
                <dd className="font-display text-xl font-semibold mt-1">{c.complaint_date}</dd>
              </div>
              <div className="p-5 border-t border-zinc-200">
                <dt
                  className={`text-[10px] uppercase tracking-widest ${
                    c.overdue ? "text-[#E63946]" : "text-zinc-500"
                  }`}
                >
                  Fecha límite
                </dt>
                <dd
                  className={`font-display text-xl font-semibold mt-1 flex items-center gap-2 ${
                    c.overdue ? "text-[#E63946]" : ""
                  }`}
                >
                  <CalendarClock size={16} /> {c.deadline}
                </dd>
              </div>
            </dl>
            {c.notes && (
              <div className="border-t border-zinc-200 p-5">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                  Notas iniciales
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{c.notes}</p>
              </div>
            )}
          </section>

          {/* Timeline */}
          <section className="border border-black bg-white">
            <div className="border-b border-black p-4 font-display font-semibold text-sm uppercase tracking-widest">
              Apuntes y seguimiento
            </div>
            <div className="p-5">
              <form onSubmit={addNote} className="mb-6">
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Añade un apunte sobre esta reclamación…"
                  className="rounded-none border-black min-h-[90px]"
                  data-testid="note-input"
                />
                <div className="flex justify-end mt-2">
                  <Button
                    type="submit"
                    disabled={savingNote || !noteText.trim()}
                    className="rounded-none btn-primary h-10 text-xs uppercase tracking-widest"
                    data-testid="add-note-btn"
                  >
                    {savingNote ? <Loader2 className="animate-spin" size={14} /> : "Agregar apunte"}
                  </Button>
                </div>
              </form>

              <ol className="relative border-l border-zinc-300 pl-6 space-y-5" data-testid="timeline">
                {[...(c.timeline || [])].reverse().map((t) => (
                  <li key={t.id} className="relative">
                    <span
                      className={`absolute -left-[27px] top-1 w-3 h-3 ${
                        t.type === "status" ? "bg-black" : "bg-white border border-black"
                      }`}
                    />
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      {new Date(t.created_at).toLocaleString("es-ES")} · {t.author_name}
                    </div>
                    <div
                      className={`mt-1 text-sm whitespace-pre-wrap ${
                        t.type === "status" ? "font-semibold" : ""
                      }`}
                    >
                      {t.text}
                    </div>
                  </li>
                ))}
                {(c.timeline || []).length === 0 && (
                  <li className="text-sm text-zinc-500">Sin actividad aún.</li>
                )}
              </ol>
            </div>
          </section>
        </div>

        {/* Right: photos */}
        <div className="lg:col-span-2">
          <section className="border border-black bg-white">
            <div className="border-b border-black p-4 flex items-center justify-between">
              <div className="font-display font-semibold text-sm uppercase tracking-widest">
                Evidencias ({c.photos?.length || 0})
              </div>
              <Button
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
                className="rounded-none btn-primary h-9 text-[11px] uppercase tracking-widest"
                data-testid="add-photos-btn"
              >
                {uploading ? (
                  <Loader2 className="animate-spin" size={14} />
                ) : (
                  <>
                    <ImagePlus size={14} className="mr-2" /> Subir
                  </>
                )}
              </Button>
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => onUpload(e.target.files)}
                data-testid="photo-upload-input"
              />
            </div>
            <div className="p-4">
              {(c.photos || []).length === 0 ? (
                <div className="text-center text-zinc-500 text-sm py-8">
                  Aún no hay evidencias. Sube las que necesites.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="photo-grid">
                  {c.photos.map((p) => {
                    return (
                      <div
                        key={p.id}
                        className="relative aspect-square border border-black overflow-hidden group"
                      >
                        <img
                          src={photoSrc(p.id)}
                          alt={p.original_filename}
                          loading="lazy"
                          className="w-full h-full object-cover cursor-zoom-in transition-transform group-hover:scale-105"
                          onClick={() => setZoomed(p)}
                          data-testid={`photo-${p.id}`}
                        />
                        <div className="absolute top-1 right-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeletePhoto(p.id);
                            }}
                            className="bg-black/80 text-white p-1 hover:bg-black"
                            title="Eliminar"
                            data-testid={`delete-photo-${p.id}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <Dialog open={!!zoomed} onOpenChange={(o) => !o && setZoomed(null)}>
        <DialogContent className="max-w-5xl rounded-none p-0 border-black bg-black">
          {zoomed && (
            <img
              src={photoSrc(zoomed.id)}
              alt={zoomed.original_filename}
              className="w-full h-auto max-h-[85vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
